import React, { useState } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { calculatePercentChange } from 'src/utils/app';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

enum TrafficTitleTypeDisplayName {
  totalClicks = 'Search Traffic',
  organicClicks = 'Organic Traffic',
  adClicks = 'Paid Search Traffic',
  otherClicks = 'Other Traffic'
}

enum TrafficSubtitleTypeDisplayName {
  totalClicks = 'both organic and paid search traffic',
  organicClicks = 'organic traffic',
  adClicks = 'paid search traffic',
  otherClicks = 'other traffic'
}

const AtlasV2lTrafficV2BrandedUnbrandedSplineChart = ({ fieldName }: { fieldName: string }) => {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const [branding, setBranding] = useState<'branded' | 'unbranded'>('branded');
  const taxonomyText = useTaxonomySubtitleText();

  const titleComponent = (
    <Flex gap="sm" alignItems="center">
      <SlDropdownMenu
        buttonVariant="title"
        options={[
          { id: 'unbranded', label: 'Unbranded' },
          { id: 'branded', label: 'Branded' }
        ]}
        defaultLabel=""
        onChange={(option) => setBranding(option.id as 'branded' | 'unbranded')}
        selectedId={branding}
      />
      <Text variant="h4">{`Share of ${TrafficTitleTypeDisplayName[fieldName]}`}</Text>
    </Flex>
  );

  return (
    <AtlasV2DorisSplineChart
      queryKey="shareOfBrandedUnbrandedTrafficSplineChart"
      title="Share of Total Traffic"
      metricTypeDisplay={METRICTYPE.PERCENT}
      splineChartProps={{ chartContextId: CHART_NAMES.BRANDED_UNBRANDED_SPLINE }}
      marketShareNumeratorRequestBuilder={(params) => {
        return {
          ...params,
          brandIds: [],
          branding: branding
        };
      }}
      metricType={fieldName}
      showMarketShare
      beaconChartWithLegendOverride={(props, metric) => {
        const { formattedPercentChange, primaryTotal, secondaryTotal } = metric;

        const percentChange = calculatePercentChange(primaryTotal, secondaryTotal);
        const percentDirection = percentChange >= 0 ? 'higher' : 'lower';

        return {
          ...props,
          includeSummaryInfo: false,
          title: titleComponent,
          loadingComponent: <SplineChartLoading includeSubtitle />,
          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1" lineHeight="24px">
                {`The chart displays the percentage of ${TrafficSubtitleTypeDisplayName[fieldName]} that is ${branding} for 
                  ${entityPhrase} ${taxonomyText} during the ${mainTimePeriodShortDisplayName} period, which is ${formattedPercentChange} ${percentDirection} compared to the ${comparisonTimePeriodShortDisplayName}.`}
                <SubtitleFilters showFilterNumber={false} />
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
    />
  );
};

export default AtlasV2lTrafficV2BrandedUnbrandedSplineChart;
